import {
  getAllAccessibleViews,
  getAppViewLabel,
  type AppView,
  type TeamCapability,
  type TeamRole,
  type UserLevel,
  type UserRole,
  type ViewEligibility,
} from '@aegislab/shared';
import { isHiddenView, isTeaserView } from './viewState';

export type AppNavSectionId = 'self' | 'team' | 'organization' | 'platform';

export interface AppNavItemMeta {
  id: AppView;
  section: AppNavSectionId;
  order: number;
  label: string;
}

export interface AppNavSection {
  id: AppNavSectionId;
  label: string;
  items: AppNavItemMeta[];
}

const SECTION_LABELS: Record<AppNavSectionId, string> = {
  self: '내 작업',
  team: '팀',
  organization: '전사 운영',
  platform: '시스템',
};

export const NAV_ITEMS: Array<Pick<AppNavItemMeta, 'id' | 'section' | 'order'>> = [
  { id: 'today', section: 'self', order: 5 },
  { id: 'attendance', section: 'self', order: 10 },
  { id: 'chat', section: 'self', order: 30 },
  { id: 'bug-reports', section: 'self', order: 40 },
  { id: 'wiki', section: 'self', order: 50 },
  // 내 몫을 보는 창이라 「내 작업」에 둔다. 몫은 부서가 아니라 단계를 옮긴 기록으로 정해져 전원에게 보인다.
  { id: 'campaign-bonus', section: 'self', order: 60 },
  { id: 'leaderboard', section: 'self', order: 80 },
  { id: 'team-settings', section: 'team', order: 10 },
  // 시나리오: 팀 스코프 운영 도구(ScenarioPage 가 deriveSpineScenarioTeamId 로 팀별 시야 분리)라
  // 주 운영자가 팀장 — 전사급 '전사 운영'(재무/대표 콘솔 등)보다 '팀' 섹션이 고도상 정확. (2026-06-16)
  { id: 'scenarios', section: 'team', order: 20 },
  // 관계 층은 캠페인(시나리오) 옆에 붙는 팀 스코프 운영 도구다 — 전사 결재 표면이 아니다.
  { id: 'creator-relations', section: 'team', order: 30 },
  // 「팀」 섹션 넷째 — 시딩 팀 팀장 자격이 있는 사람에게만 보인다(docs/design/ia/creator-lists-ia.md §1).
  { id: 'creator-lists', section: 'team', order: 40 },
  { id: 'admin-attendance', section: 'organization', order: 10 },
  { id: 'hr-management', section: 'organization', order: 20 },
  { id: 'finance', section: 'organization', order: 30 },
  { id: 'advertiser-console', section: 'organization', order: 25 },
  // 재무(30) 옆 — 돈이 나가는 표면이라 같은 이웃이 맞고, 여는 사람은 피플팀 실행자뿐이다.
  { id: 'campaign-settlement', section: 'organization', order: 32 },
  // 해외 정산 실행 바로 옆 — 국내 캠페인 정산을 3팀장이 처리하는 창이다(fact #2902).
  { id: 'domestic-settlement', section: 'organization', order: 33 },
  { id: 'ceo-console', section: 'organization', order: 35 },
  // 대표 콘솔 바로 아래 — 대표와 개발팀이 같은 표를 본다. DEV capability 팀과 EXECUTIVE 에게 보인다.
  { id: 'usage-dashboard', section: 'organization', order: 36 },
  { id: 'system-admin', section: 'platform', order: 10 },
];

export function getAppNavSections({
  userLevel,
  teamCapabilities = [],
  roles,
  teamRole,
  viewEligibilities,
  leadsTeam = false,
}: {
  userLevel: UserLevel;
  teamCapabilities?: TeamCapability[];
  /**
   * 역할 요건이 걸린 뷰(대표 콘솔)를 목록에서 가르는 축. 창 본문만 막고 이 목록이 새면
   * "창조차 안 보인다"가 깨져 열리지 않는 항목이 독에 남는다.
   *
   * 선택 인자로 두지 않는다 — 기본값을 두면 호출부가 빼먹었을 때 대표가 자기 창을 잃는데,
   * 그건 타입체크가 못 잡는다.
   */
  roles: UserRole[];
  /** 권한 요건이 걸린 뷰(근태 운영)는 팀 직책이 권한을 주므로 목록도 그 축을 본다. */
  teamRole?: TeamRole;
  /**
   * 서버가 판정한 창 자격(명단 내려받기). 선택 인자로 두지 않는다 — 빠뜨리면 자격 있는 팀장의
   * 독에서 그 창이 조용히 빠진다.
   */
  viewEligibilities: ViewEligibility[];
  /** 어느 팀(TF 포함)에서든 팀장인가 — 토큰 teamRole 은 부서만 담아 TF 팀장을 못 싣는다. */
  leadsTeam?: boolean;
}): AppNavSection[] {
  const accessibleViews = new Set(
    getAllAccessibleViews(
      userLevel,
      teamCapabilities,
      roles,
      teamRole,
      viewEligibilities,
      leadsTeam
    )
  );

  const visibleItems = NAV_ITEMS.filter((item) => {
    if (isHiddenView(item.id)) return false;
    return accessibleViews.has(item.id);
  })
    .map((item) => ({
      ...item,
      label: getAppViewLabel(item.id),
    }))
    .sort((a, b) => {
      const aTeaser = isTeaserView(a.id, teamCapabilities) ? 1 : 0;
      const bTeaser = isTeaserView(b.id, teamCapabilities) ? 1 : 0;
      if (aTeaser !== bTeaser) return aTeaser - bTeaser;
      return a.order - b.order;
    });

  const sectionOrder: AppNavSectionId[] = ['self', 'team', 'organization', 'platform'];

  return sectionOrder
    .map((sectionId) => ({
      id: sectionId,
      label: SECTION_LABELS[sectionId],
      items: visibleItems.filter((item) => item.section === sectionId),
    }))
    .filter((section) => section.items.length > 0);
}
